import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import memoizeOne from 'memoize-one';

import { request } from 'app/utils/request';
import { getApiUrl, getTangerineHeaders, getToken } from 'app/utils/api';
import { removeItemWhen } from 'app/utils/array';
import { hasAccessToFeature } from 'app/utils/permissions';
import type { Blueprint } from 'app/pages/BlueprintsPage/types';

interface WSInsertMessage {
  type: 'blueprint';
  action: 'inserted';
  model: Blueprint;
}

interface WSUpdateMessage {
  type: 'blueprint';
  action: 'updated';
  model: Blueprint;
}

interface WSDeleteMessage {
  type: 'blueprint';
  action: 'deleted';
  model: Blueprint;
}

type WSBlueprintMessage = WSInsertMessage | WSUpdateMessage | WSDeleteMessage;

function getBlueprintsRequest(userType: UserType) {
  const token = getToken();
  // if user is trial we don't need to do the request
  if (!hasAccessToFeature('BLUEPRINTS', userType)) {
    return of([]);
  }

  return createBlueprintsRequest(token);
}

/**
 * Function is memoize with token because we need to recreate a request observer only when token change
 * even if `token` param isn't used, getTangerineHeaders depends on current token
 * this made betting group request observer cached at module level
 * so several components can use the same request observer reference
 */
// @ts-ignore
const createBlueprintsRequest = memoizeOne((token: string) =>
  request<Blueprint[]>({
    url: getApiUrl('/blueprints'),
    headers: getTangerineHeaders()
  }).pipe(map(response => response.data!))
);

function handleBlueprintMessage(
  currentData: Blueprint[],
  message: WSBlueprintMessage
): Observable<Blueprint[]> {
  switch (message.action) {
    case 'inserted': {
      return of([...currentData, message.model]);
    }
    case 'updated': {
      const updatedList = currentData.map(blueprint => {
        if (blueprint.id === message.model.id) {
          return message.model;
        }
        return blueprint;
      });

      return of(updatedList);
    }
    case 'deleted': {
      return of(removeItemWhen(currentData, ({ id }) => id === message.model.id));
    }
    default: {
      return of(currentData);
    }
  }
}

export { getBlueprintsRequest, handleBlueprintMessage };
